import { ref, uploadBytesResumable, type UploadTask } from 'firebase/storage';
import { doc, onSnapshot, collection, query, where, getDocs, getDoc, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, storage } from './firebase';
import { COLLECTIONS } from '../constants/collections';
import type { ContentDocument, ProcessingStatus, PriorKnowledgeAnchor } from '../types/content';

// Initialize Firebase Functions lazily
let _functions: ReturnType<typeof import('firebase/functions').getFunctions> | null = null;
async function getFunctionsInstance() {
  if (!_functions) {
    const { getFunctions } = await import('firebase/functions');
    const { app } = await import('./firebase');
    _functions = getFunctions(app);
  }
  return _functions;
}

export function uploadContent(
  userId: string,
  file: File,
): { contentId: string; uploadTask: UploadTask } {
  const contentId = `doc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const storagePath = `uploads/${userId}/${contentId}/${file.name}`;
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type,
  });
  return { contentId, uploadTask };
}

export function onProcessingStatusChange(
  contentId: string,
  callback: (status: ProcessingStatus, document: ContentDocument) => void,
): () => void {
  const docRef = doc(db, COLLECTIONS.CONTENT, contentId);
  return onSnapshot(docRef, (snapshot) => {
    if (!snapshot.exists()) return;
    const data = snapshot.data() as ContentDocument;
    callback(data.processingStatus, data);
  });
}

export async function submitPriorKnowledge(
  contentId: string,
  selfAssessment: string,
): Promise<PriorKnowledgeAnchor> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<
    { contentId: string; selfAssessment: string },
    PriorKnowledgeAnchor
  >(functions, 'assessPriorKnowledge');
  const result = await fn({ contentId, selfAssessment });
  return result.data;
}

export async function triggerEmbeddings(contentId: string): Promise<void> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable(functions, 'prepareEmbeddings');
  await fn({ contentId });
}

export async function loadContent(contentId: string): Promise<ContentDocument | null> {
  const docRef = doc(db, COLLECTIONS.CONTENT, contentId);
  const snapshot = await getDoc(docRef);
  if (!snapshot.exists()) return null;
  return snapshot.data() as ContentDocument;
}

/**
 * Hard-deletes a user's uploaded content and everything derived from it:
 * learning paths + their nodes, tutor sessions, uploaded storage files,
 * and the content doc itself. Server-side (Cloud Function) so rules +
 * storage + subcollection cleanup stay consistent.
 */
export async function deleteUserContent(contentId: string): Promise<{
  contentId: string;
  deletedPaths: number;
  deletedNodes: number;
  deletedScoped: number;
  deletedFiles: number;
}> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<
    { contentId: string },
    {
      contentId: string;
      deletedPaths: number;
      deletedNodes: number;
      deletedScoped: number;
      deletedFiles: number;
    }
  >(functions, 'deleteUserContent');
  const result = await fn({ contentId });
  return result.data;
}

export async function loadUserContent(userId: string): Promise<ContentDocument[]> {
  const q = query(
    collection(db, COLLECTIONS.CONTENT),
    where('userId', '==', userId),
    orderBy('uploadDate', 'desc'),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => d.data() as ContentDocument);
}
